import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Story } from '../../schemas/story.js';

import { locatorOf, toPointer } from '../../core/issues.js';
import { extractOutcomeIds } from '../story/outcomesMatchExitRoutes.js';

/**
 * Every outcome a day-in-the-life activity can finish with has somewhere to go: its own route, or
 * the node's `default`, which the player falls back to. An outcome with neither ends the piece.
 */
export const dilOutcomesRouted: Rule<Story> = {
  code: 'dil.outcomes',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    input.nodes.forEach((node, nodeIdx) => {
      const activity = (node as unknown as Record<string, unknown>).activity as Parameters<typeof extractOutcomeIds>[0];
      if (!activity || node.nodeType !== 'activity') {
        return;
      }
      const routes = new Set(Object.keys(node.exitRoutes ?? {}));
      if (routes.has('default')) {
        return;
      }
      for (const outcomeId of extractOutcomeIds(activity)) {
        if (routes.has(outcomeId)) {
          continue;
        }
        const path: (string | number)[] = ['nodes', nodeIdx, 'exitRoutes', outcomeId];
        issues.push({
          code: 'dil.outcomes.missingExitRoute',
          severity: 'error',
          message: `Node ${node.id}: its activity can finish with '${outcomeId}', which has no route and no default, so the piece would end there.`,
          path,
          pointer: toPointer(path),
          locator: locatorOf({ nodeId: node.id }),
          currentValue: outcomeId,
          constraint: { kind: 'shape', detail: 'every activity outcome needs an exitRoute or a default' },
        });
      }
    });
    return issues;
  },
};
